/**
 * Modal for per-invitation employer/admin fields (記入書類) before send.
 */
import React, { useEffect, useState } from 'react';
import {
  getEmployerFieldsForForm,
  getEmployerContextForForm,
  normalizeFormCode,
  type FormEmployerContext,
} from '../../invitationEmployerFields';
import './InvitationFormEmployerModal.css';

export interface InvitationFormEmployerModalProps {
  isOpen: boolean;
  formCode: string | null;
  formNameJa: string;
  context: FormEmployerContext;
  onClose: () => void;
  onSave: (formCode: string, fields: Record<string, string>) => void;
}

const InvitationFormEmployerModal: React.FC<InvitationFormEmployerModalProps> = ({
  isOpen,
  formCode,
  formNameJa,
  context,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen || !formCode) return;
    const normalizedCode = normalizeFormCode(formCode);
    const defs = getEmployerFieldsForForm(normalizedCode);
    const existing = getEmployerContextForForm(context, normalizedCode) ?? {};
    const initial: Record<string, string> = {};
    for (const def of defs) {
      initial[def.key] = existing[def.key] ?? '';
    }
    setDraft(initial);
  }, [isOpen, formCode, context]);

  if (!isOpen || !formCode) return null;

  const normalizedCode = normalizeFormCode(formCode);
  const fieldDefs = getEmployerFieldsForForm(normalizedCode);
  const canSave = fieldDefs
    .filter((d) => d.required)
    .every((d) => (draft[d.key] ?? '').trim().length > 0);

  const handleSave = () => {
    if (!canSave) return;
    const trimmed: Record<string, string> = {};
    for (const def of fieldDefs) {
      trimmed[def.key] = (draft[def.key] ?? '').trim();
    }
    onSave(normalizedCode, trimmed);
    onClose();
  };

  return (
    <div
      className="inv-form-employer-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="inv-form-employer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inv-form-employer-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-form-employer-modal__header">
          <h2 id="inv-form-employer-modal-title" className="inv-form-employer-modal__title">
            管理者入力
          </h2>
          <button
            type="button"
            className="inv-form-employer-modal__close-btn"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        <div className="inv-form-employer-modal__body">
          <p className="inv-form-employer-modal__form-name">{formNameJa}</p>
          <p className="inv-form-employer-modal__hint">
            管理者入力は任意です。未入力のまま招待を送信でき、後から編集で追加できます。入力内容はPDFに反映されます。
          </p>
              {fieldDefs.map((def) => (
            <div key={def.key} className="inv-form-employer-modal__field">
              <label className="inv-form-employer-modal__label" htmlFor={`employer-${def.key}`}>
                {def.labelJa}
                {def.required && (
                  <span className="inv-form-employer-modal__required" aria-hidden>
                    ＊
                  </span>
                )}
              </label>
              {def.multiline ? (
                <textarea
                  id={`employer-${def.key}`}
                  className="inv-form-employer-modal__textarea"
                  value={draft[def.key] ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [def.key]: e.target.value }))
                  }
                  rows={3}
                  autoComplete="off"
                />
              ) : (
                <input
                  id={`employer-${def.key}`}
                  type={def.inputType === 'date' ? 'date' : 'text'}
                  className="inv-form-employer-modal__input"
                  value={draft[def.key] ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [def.key]: e.target.value }))
                  }
                  autoComplete="off"
                />
              )}
            </div>
          ))}
        </div>

        <div className="inv-form-employer-modal__footer">
          <button
            type="button"
            className="inv-form-employer-modal__cancel-btn"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="inv-form-employer-modal__save-btn"
            onClick={handleSave}
            disabled={!canSave}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitationFormEmployerModal;
